/**
 * AI Player for Pocket Rockets
 * Loads trained TensorFlow.js model and plays the game
 */

import * as tf from '@tensorflow/tfjs';

export class AIPlayer {
    constructor(difficulty = 'medium') {
        this.difficulty = difficulty;
        this.model = null;
        this.isLoaded = false;
        this.stateDim = 61;  // Updated: 58 + 3 expert features (theta_lead_err, target_in_range, ttk_active)
        this.actionDim = 7;
        
        // Action mapping (matches training)
        this.actions = [
            { name: 'TURN_LEFT', keys: { left: true } },
            { name: 'TURN_RIGHT', keys: { right: true } },
            { name: 'THRUST', keys: { up: true } },
            { name: 'SHOOT', keys: { space: true } },
            { name: 'STRAFE_LEFT', keys: { q: true } },
            { name: 'STRAFE_RIGHT', keys: { e: true } },
            { name: 'NO_OP', keys: {} }
        ];
    }
    
    /**
     * Load trained model
     * Autopilot uses checkpoint_last50 from latest training run
     * @returns {Promise<void>}
     */
    async load() {
        try {
            // Autopilot uses the latest training run's checkpoint_last50
            // This path should be updated by export script to point to latest run
            const modelPath = '/models/latest/checkpoint_last50/model.json';
            console.log(`[AIPlayer] Loading latest checkpoint (checkpoint_last50)...`);
            this.model = await tf.loadLayersModel(modelPath);
            this.isLoaded = true;
            console.log(`[AIPlayer] Model loaded successfully from checkpoint_last50!`);
        } catch (error) {
            console.error(`[AIPlayer] Failed to load model:`, error);
            throw error;
        }
    }
    
    /**
     * Get action from model
     * @param {Object} gameCore - GameCore instance
     * @returns {Object} Action keys {left, right, up, down, space, q, e}
     */
    getAction(gameCore) {
        if (!this.isLoaded) {
            console.warn('[AIPlayer] Model not loaded');
            return {};
        }
        
        // Extract state from GameCore
        const state = this.extractState(gameCore);
        
        // Get action from model
        const actionIndex = tf.tidy(() => {
            const stateTensor = tf.tensor2d([state], [1, this.stateDim]);
            const qValues = this.model.predict(stateTensor);
            const action = qValues.argMax(-1).dataSync()[0];
            return action;
        });
        
        // Return corresponding keys
        return this.actions[actionIndex].keys;
    }
    
    /**
     * Extract state vector from GameCore
     * Must match GameEnv.getState() exactly!
     * @param {Object} gameCore - GameCore instance
     * @returns {Array} State vector
     */
    extractState(gameCore) {
        const state = new Array(this.stateDim).fill(0);
        let idx = 0;
        
        const ship = gameCore.ship;
        const worldWidth = gameCore.worldWidth;
        const worldHeight = gameCore.worldHeight;
        
        // === SHIP STATE (8 features) ===
        state[idx++] = ship.pos.x / worldWidth;
        state[idx++] = ship.pos.y / worldHeight;
        state[idx++] = ship.vel.x / 0.2;  // SHIP_VEL_MAX_MAGNITUDE
        state[idx++] = ship.vel.y / 0.2;
        state[idx++] = Math.cos(ship.angle);
        state[idx++] = Math.sin(ship.angle);
        state[idx++] = ship.health / ship.maxHealth;
        state[idx++] = ship.bulletCooldown / 6;  // BULLET_COOLDOWN
        
        // === NEAREST ASTEROIDS (5 × 8 = 40 features) ===
        const asteroids = this.getNearestAsteroids(gameCore, 5);
        for (let i = 0; i < 5; i++) {
            if (i < asteroids.length) {
                const a = asteroids[i];
                const dx = a.pos.x - ship.pos.x;
                const dy = a.pos.y - ship.pos.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                const angle = Math.atan2(dy, dx);
                
                state[idx++] = dx / worldWidth;
                state[idx++] = dy / worldHeight;
                state[idx++] = dist / Math.sqrt(worldWidth ** 2 + worldHeight ** 2);
                state[idx++] = Math.cos(angle);
                state[idx++] = Math.sin(angle);
                state[idx++] = a.vel.x / 0.2;
                state[idx++] = a.vel.y / 0.2;
                state[idx++] = a.health / 250;  // Max boss health
            } else {
                idx += 8;
            }
        }
        
        // === NEAREST COLLECTIBLES (2 × 5 = 10 features) ===
        const collectibles = this.getNearestCollectibles(gameCore, 2);
        for (let i = 0; i < 2; i++) {
            if (i < collectibles.length) {
                const c = collectibles[i];
                const dx = c.pos.x - ship.pos.x;
                const dy = c.pos.y - ship.pos.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                
                state[idx++] = dx / worldWidth;
                state[idx++] = dy / worldHeight;
                state[idx++] = dist / Math.sqrt(worldWidth ** 2 + worldHeight ** 2);
                state[idx++] = c.type === 'ore' ? 1 : 0;
                state[idx++] = c.type === 'health' ? 1 : 0;
            } else {
                idx += 5;
            }
        }
        
        // === EXPERT QUICK WINS: TARGETING FEATURES (3 features) ===
        const targeting = this.getTargetingFeatures(gameCore);
        state[idx++] = targeting.thetaLeadErr / Math.PI;  // Normalized to [-1, 1]
        state[idx++] = targeting.targetInRange;  // 0 or 1
        state[idx++] = Math.min(targeting.ttkActive / 10.0, 1.0);  // Normalized, capped at 10s
        
        return state;
    }
    
    /**
     * Get N nearest asteroids to ship
     */
    getNearestAsteroids(gameCore, n) {
        const ship = gameCore.ship;
        return gameCore.asteroids
            .map(a => {
                const dx = a.pos.x - ship.pos.x;
                const dy = a.pos.y - ship.pos.y;
                const distSq = dx * dx + dy * dy;
                return { ...a, distSq };
            })
            .sort((a, b) => a.distSq - b.distSq)
            .slice(0, n);
    }
    
    /**
     * Get N nearest collectibles to ship
     */
    getNearestCollectibles(gameCore, n) {
        const ship = gameCore.ship;
        return gameCore.dropItems
            .map(c => {
                const dx = c.pos.x - ship.pos.x;
                const dy = c.pos.y - ship.pos.y;
                const distSq = dx * dx + dy * dy;
                return { ...c, distSq };
            })
            .sort((a, b) => a.distSq - b.distSq)
            .slice(0, n);
    }
    
    /**
     * Calculate lead angle for shooting moving target
     * @param {Object} ship - Ship object
     * @param {Object} asteroid - Asteroid object
     * @returns {number} Lead angle in radians
     */
    calculateLeadAngle(ship, asteroid) {
        const bulletSpeed = 10.0;  // From game constants
        const dx = asteroid.pos.x - ship.pos.x;
        const dy = asteroid.pos.y - ship.pos.y;
        const vx = asteroid.vel.x;
        const vy = asteroid.vel.y;
        
        // Solve for time t where bullet intercepts asteroid
        const a = vx * vx + vy * vy - bulletSpeed * bulletSpeed;
        const b = 2 * (dx * vx + dy * vy);
        const c = dx * dx + dy * dy;
        
        const discriminant = b * b - 4 * a * c;
        if (discriminant < 0 || Math.abs(a) < 0.0001) {
            // No intercept or stationary target - aim directly
            return Math.atan2(dy, dx);
        }
        
        const t = (-b - Math.sqrt(discriminant)) / (2 * a);
        if (t < 0) {
            // Target moving away faster than bullet - aim directly
            return Math.atan2(dy, dx);
        }
        
        // Calculate lead position
        const leadX = asteroid.pos.x + vx * t;
        const leadY = asteroid.pos.y + vy * t;
        return Math.atan2(leadY - ship.pos.y, leadX - ship.pos.x);
    }
    
    /**
     * Normalize angle to [-π, π]
     * @param {number} angle - Angle in radians
     * @returns {number} Normalized angle
     */
    normalizeAngle(angle) {
        while (angle > Math.PI) angle -= 2 * Math.PI;
        while (angle < -Math.PI) angle += 2 * Math.PI;
        return angle;
    }
    
    /**
     * Get theta_lead_err, target_in_range, ttk_active for state
     * @param {Object} gameCore - GameCore instance
     * @returns {Object} {thetaLeadErr, targetInRange, ttkActive}
     */
    getTargetingFeatures(gameCore) {
        const ship = gameCore.ship;
        const asteroids = this.getNearestAsteroids(gameCore, 1);
        const nearestAsteroid = asteroids[0];
        
        if (!nearestAsteroid) {
            return {
                thetaLeadErr: 0,
                targetInRange: 0,
                ttkActive: 0
            };
        }
        
        // Calculate lead angle
        const leadAngle = this.calculateLeadAngle(ship, nearestAsteroid);
        const thetaLeadErr = this.normalizeAngle(leadAngle - ship.angle);
        
        // Check if target is in range (bullet lifetime ~3 seconds, speed 10 = 300 units)
        const dx = nearestAsteroid.pos.x - ship.pos.x;
        const dy = nearestAsteroid.pos.y - ship.pos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const bulletRange = 300;  // 10 speed * 30 frames (approx bullet lifetime)
        const targetInRange = dist <= bulletRange ? 1 : 0;
        
        // TTK active - simplified: use 0 for now (would need engagement tracking)
        // In training, this tracks time since first hit, but for inference we can use 0
        // The model should still work without this exact value
        const ttkActive = 0;
        
        return { thetaLeadErr, targetInRange, ttkActive };
    }
    
    /**
     * Dispose model and clean up
     */
    dispose() {
        if (this.model) {
            this.model.dispose();
            this.model = null;
            this.isLoaded = false;
            console.log('[AIPlayer] Model disposed');
        }
    }
}


